import { useEffect, useMemo, useState } from 'react';
import {
  fetchAssignmentGroupLabelsForCourse,
  fetchCoursePlacementLabels,
  getAssignmentGroupDisplayName,
  getCourseGroupDisplayName,
  getStreamDisplayName
} from '../../../utils/groupLabels';

import {
  buildAttendanceContributionRows,
  buildAttendanceContributionTableHtml,
  formatAttendanceContribution,
  normalizeAttendanceSettings
} from '../../../utils/attendanceCalculations';

import {
  normalClassroomForDate
} from '../../../utils/classroomResolution';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function apiHeaders(extra = {}) {
  return {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY,
    ...extra
  };
}

async function readJson(response, label) {
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`${label} failed (${response.status}): ${detail}`);
  }

  return response.json();
}

async function fetchWeeks(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&select=*&order=week_number.asc`,
    { headers: apiHeaders() }
  );

  return readJson(response, 'Loading attendance schedule');
}

async function updateWeek(weekId, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?id=eq.${weekId}`,
    {
      method: 'PATCH',
      headers: apiHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Updating attendance week failed (${response.status}): ${detail}`
    );
  }
}

async function updateCourse(courseId, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/courses?id=eq.${courseId}`,
    {
      method: 'PATCH',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      }),
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Updating attendance settings failed (${response.status}): ${detail}`
    );
  }

  const rows = await response.json();

  if (
    !Array.isArray(rows) ||
    rows.length === 0
  ) {
    throw new Error(
      'Attendance settings were not saved: no course row was updated.'
    );
  }

  return rows[0];
}

async function fetchEnrolments(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?course_id=eq.${courseId}&active=eq.true&select=id,student_id,stream,group_number,students(id,student_code,english_name,chinese_name,is_master)&order=created_at.asc`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(response, 'Loading students');

  return (Array.isArray(rows) ? rows : [])
    .filter(
      enrolment =>
        enrolment?.students?.is_master !== true
    )
    .sort((a, b) => {
    const aCode = String(a.students?.student_code || '');
    const bCode = String(b.students?.student_code || '');

    return aCode.localeCompare(
      bCode,
      undefined,
      { numeric: true, sensitivity: 'base' }
    );
  });
}

async function fetchAttendanceGroups(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_groups?course_id=eq.${courseId}&select=*&order=group_number.asc`,
    { headers: apiHeaders() }
  );

  return readJson(response, 'Loading attendance groups');
}

async function fetchAttendanceMemberships(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_group_memberships?select=*,attendance_groups!inner(course_id)&attendance_groups.course_id=eq.${courseId}`,
    { headers: apiHeaders() }
  );

  return readJson(response, 'Loading attendance group memberships');
}

async function fetchAttendanceGroupWeeks(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_group_weeks?select=*,attendance_groups!inner(course_id)&attendance_groups.course_id=eq.${courseId}`,
    { headers: apiHeaders() }
  );

  return readJson(response, 'Loading attendance group schedule');
}

async function createAttendanceGroup(courseId, groupNumber) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_groups`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      }),
      body: JSON.stringify({
        course_id: courseId,
        group_number: groupNumber
      })
    }
  );

  return readJson(response, 'Creating attendance group');
}

async function deleteAttendanceGroup(groupId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_groups?id=eq.${groupId}`,
    {
      method: 'DELETE',
      headers: apiHeaders()
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Deleting attendance group failed (${response.status}): ${detail}`
    );
  }
}

async function addAttendanceMemberships(groupId, enrolmentIds) {
  if (!enrolmentIds.length) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_group_memberships`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      }),
      body: JSON.stringify(
        enrolmentIds.map(enrolmentId => ({
          attendance_group_id: groupId,
          enrolment_id: enrolmentId
        }))
      )
    }
  );

  return readJson(response, 'Adding students to attendance group');
}

async function removeAttendanceMembership(groupId, enrolmentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_group_memberships?attendance_group_id=eq.${groupId}&enrolment_id=eq.${enrolmentId}`,
    {
      method: 'DELETE',
      headers: apiHeaders()
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Removing student from attendance group failed (${response.status}): ${detail}`
    );
  }
}

async function deleteMembershipsForGroup(groupId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_group_memberships?attendance_group_id=eq.${groupId}`,
    {
      method: 'DELETE',
      headers: apiHeaders()
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Clearing attendance group failed (${response.status}): ${detail}`
    );
  }
}

async function deleteMembershipsForEnrolments(enrolmentIds) {
  if (!enrolmentIds.length) return;

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_group_memberships?enrolment_id=in.(${enrolmentIds.join(',')})`,
    {
      method: 'DELETE',
      headers: apiHeaders()
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Reassigning attendance group members failed (${response.status}): ${detail}`
    );
  }
}

async function addGroupWeeks(groupId, weekIds) {
  if (!weekIds.length) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_group_weeks?on_conflict=attendance_group_id,course_week_id`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates,return=representation'
      }),
      body: JSON.stringify(
        weekIds.map(courseWeekId => ({
          attendance_group_id: groupId,
          course_week_id: courseWeekId
        }))
      )
    }
  );

  return readJson(response, 'Saving attendance group schedule');
}

async function removeGroupWeek(groupId, weekId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_group_weeks?attendance_group_id=eq.${groupId}&course_week_id=eq.${weekId}`,
    {
      method: 'DELETE',
      headers: apiHeaders()
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Updating attendance group schedule failed (${response.status}): ${detail}`
    );
  }
}

async function updateGroupWeek(groupId, weekId, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/attendance_group_weeks?attendance_group_id=eq.${groupId}&course_week_id=eq.${weekId}`,
    {
      method: 'PATCH',
      headers: apiHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Updating attendance group timing failed (${response.status}): ${detail}`
    );
  }
}

function weekdayShort(dateValue) {
  const parts = String(dateValue || '')
    .slice(0, 10)
    .split('-')
    .map(Number);

  if (
    parts.length !== 3 ||
    !parts.every(Number.isFinite)
  ) {
    return '';
  }

  return new Date(
    parts[0],
    parts[1] - 1,
    parts[2],
    12,
    0,
    0
  ).toLocaleDateString(
    'en-GB',
    { weekday: 'short' }
  );
}

function normalCourseTime(course, dateValue) {
  const parts = String(dateValue || '')
    .slice(0, 10)
    .split('-')
    .map(Number);

  if (
    parts.length !== 3 ||
    !parts.every(Number.isFinite)
  ) {
    return String(
      course?.class_time || ''
    ).slice(0, 5);
  }

  const day = new Date(
    parts[0],
    parts[1] - 1,
    parts[2],
    12,
    0,
    0
  ).toLocaleDateString(
    'en-US',
    { weekday: 'long' }
  );

  return String(
    course?.class_times?.[day] ||
    course?.class_time ||
    ''
  ).slice(0, 5);
}

async function fetchCourseAssignments(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignments?course_id=eq.${courseId}&select=id,assignment_type_id,instance_number,groups_enabled,group_count,assignment_types(id,name,requires_submission,is_core)&order=instance_number.asc`,
    { headers: apiHeaders() }
  );

  return readJson(response, 'Loading assignment groups');
}

async function fetchAssignmentGroupMemberships(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_group_memberships?select=course_assignment_id,enrolment_id,group_number,course_assignments!inner(course_id)&course_assignments.course_id=eq.${courseId}`,
    { headers: apiHeaders() }
  );

  return readJson(response, 'Loading assignment group memberships');
}

async function fetchAssignmentTypes() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_types?select=id,name,is_core,requires_submission`,
    { headers: apiHeaders() }
  );

  return readJson(response, 'Loading assignment types');
}

async function fetchAssignmentWeights(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_weights?course_id=eq.${courseId}&select=*`,
    { headers: apiHeaders() }
  );

  return readJson(response, 'Loading assignment weights');
}

function studentLabel(enrolment) {
  const student = enrolment?.students;

  return [
    student?.student_code || '—',
    student?.english_name || student?.chinese_name || ''
  ]
    .filter(Boolean)
    .join(' — ');
}

function assignmentLabel(assignment, allAssignments) {
  const name = assignment.assignment_types?.name || 'Assignment';
  const sameType = allAssignments.filter(
    row => row.assignment_type_id === assignment.assignment_type_id
  );

  if (sameType.length > 1) {
    return `${name} ${Number(assignment.instance_number) || 1}`;
  }

  return name;
}

function Toggle({ checked, onChange, disabled = false }) {
  return (
    <label
      style={{
        ...styles.toggle,
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'default' : 'pointer'
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={event => onChange(event.target.checked)}
      />
      <span>{checked ? 'On' : 'Off'}</span>
    </label>
  );
}

export default function CourseAttendance({ course, onCourseUpdated }) {
  const [weeks, setWeeks] = useState([]);
  const [enrolments, setEnrolments] = useState([]);
  const [groups, setGroups] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [groupWeeks, setGroupWeeks] = useState([]);
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [assignmentMemberships, setAssignmentMemberships] = useState([]);
  const [assignmentGroupLabels, setAssignmentGroupLabels] =
    useState({});
  const [coursePlacementLabels, setCoursePlacementLabels] =
    useState({ streams: {}, groups: {} });
  const [assignmentTypes, setAssignmentTypes] = useState([]);
  const [groupBased, setGroupBased] = useState(
    course.group_based_attendance === true
  );
  const [automatedCheckIn, setAutomatedCheckIn] = useState(
    course.automated_checkin_enabled === true
  );

  const [variableTimings, setVariableTimings] = useState(
    course.variable_timings_enabled === true
  );

  const [variableRooms, setVariableRooms] = useState(
    course.variable_rooms_enabled === true
  );
  const [activeTab, setActiveTab] = useState('schedule');
  const [selectedIds, setSelectedIds] = useState([]);
  const [copyTargetGroupId, setCopyTargetGroupId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [weightDraft, setWeightDraft] = useState('');
  const [weightSaving, setWeightSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      fetchAssignmentGroupLabelsForCourse(course.id),
      fetchCoursePlacementLabels(course.id)
    ])
      .then(([assignmentLabels, placementLabels]) => {
        if (cancelled) return;
        setAssignmentGroupLabels(assignmentLabels || {});
        setCoursePlacementLabels(
          placementLabels || { streams: {}, groups: {} }
        );
      })
      .catch(err => {
        if (!cancelled) {
          console.error(
            'Loading Group display names failed:',
            err
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [course.id]);

  const initialAttendanceSettings = normalizeAttendanceSettings(course);
  const [graceDraft, setGraceDraft] = useState(
    String(initialAttendanceSettings.graceAbsences)
  );
  const [minContributionDraft, setMinContributionDraft] = useState(
    String(initialAttendanceSettings.minContribution)
  );
  const [penaltyPattern, setPenaltyPattern] = useState(
    initialAttendanceSettings.penaltyPattern
  );
  const [penaltyIncreaseDraft, setPenaltyIncreaseDraft] = useState(
    String(initialAttendanceSettings.penaltyIncreasePercent)
  );
  const [previewEnrolmentId, setPreviewEnrolmentId] = useState('');


  async function loadAll() {
    setLoading(true);
    setError('');

    try {
      const [
        loadedWeeks,
        loadedEnrolments,
        loadedGroups,
        loadedMemberships,
        loadedGroupWeeks,
        loadedAssignments,
        loadedAssignmentMemberships,
        loadedTypes,
        loadedWeights
      ] = await Promise.all([
        fetchWeeks(course.id),
        fetchEnrolments(course.id),
        fetchAttendanceGroups(course.id),
        fetchAttendanceMemberships(course.id),
        fetchAttendanceGroupWeeks(course.id),
        fetchCourseAssignments(course.id),
        fetchAssignmentGroupMemberships(course.id),
        fetchAssignmentTypes(),
        fetchAssignmentWeights(course.id)
      ]);

      setWeeks(Array.isArray(loadedWeeks) ? loadedWeeks : []);
      setEnrolments(
        Array.isArray(loadedEnrolments) ? loadedEnrolments : []
      );
      setGroups(Array.isArray(loadedGroups) ? loadedGroups : []);
      setMemberships(
        Array.isArray(loadedMemberships) ? loadedMemberships : []
      );
      setGroupWeeks(
        Array.isArray(loadedGroupWeeks) ? loadedGroupWeeks : []
      );
      setCourseAssignments(
        Array.isArray(loadedAssignments) ? loadedAssignments : []
      );
      setAssignmentMemberships(
        Array.isArray(loadedAssignmentMemberships)
          ? loadedAssignmentMemberships
          : []
      );
      setAssignmentTypes(
        Array.isArray(loadedTypes) ? loadedTypes : []
      );
      
      const attendanceType = (loadedTypes || []).find(
        type => type.name === 'Attendance'
      );
      const attendanceWeight = (loadedWeights || []).find(
        row => row.assignment_type_id === attendanceType?.id
      );

      setWeightDraft(attendanceWeight?.weight_percent ?? '');
      setSelectedIds([]);

      setPreviewEnrolmentId(current => {
        const loadedStudents = Array.isArray(loadedEnrolments)
          ? loadedEnrolments
          : [];

        if (loadedStudents.some(row => row.id === current)) {
          return current;
        }

        const assignedStudent = loadedStudents.find(enrolment =>
          (loadedMemberships || []).some(
            membership => membership.enrolment_id === enrolment.id
          )
        );

        return (assignedStudent || loadedStudents[0])?.id || '';
      });
    } catch (err) {
      console.error('Attendance settings load failed:', err);
      setError(err?.message || 'Failed to load attendance settings.');
    } finally {
      setLoading(false);
    }
  }

 useEffect(() => {
    const settings = normalizeAttendanceSettings(course);

    setGroupBased(course.group_based_attendance === true);
    setAutomatedCheckIn(course.automated_checkin_enabled === true);
    setVariableTimings(course.variable_timings_enabled === true);
    setVariableRooms(course.variable_rooms_enabled === true);
    setGraceDraft(String(settings.graceAbsences));
    setMinContributionDraft(String(settings.minContribution));
    setPenaltyPattern(settings.penaltyPattern);
    setPenaltyIncreaseDraft(String(settings.penaltyIncreasePercent));
    setActiveTab('schedule');
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course.id]);

  const membershipByEnrolment = useMemo(
    () => new Map(
      memberships.map(row => [row.enrolment_id, row])
    ),
    [memberships]
  );

  const unassigned = enrolments.filter(
    enrolment => !membershipByEnrolment.has(enrolment.id)
  );

  const attendanceType = assignmentTypes.find(
    type => type.name === 'Attendance'
  );

   function getRequiredAttendanceCount(enrolment) {
    if (!groupBased) {
      return weeks.filter(week => week.attendance_enabled).length;
    }

    if (!enrolment) return 0;

    const membership = memberships.find(
      row => row.enrolment_id === enrolment.id
    );

    if (!membership) return 0;

    return groupWeeks.filter(
      row =>
        row.attendance_group_id === membership.attendance_group_id
    ).length;
  }

  function membersForGroup(groupId) {
    const memberIds = new Set(
      memberships
        .filter(row => row.attendance_group_id === groupId)
        .map(row => row.enrolment_id)
    );

    return enrolments.filter(enrolment => memberIds.has(enrolment.id));
  }

  function groupHasWeek(groupId, weekId) {
    return groupWeeks.some(
      row =>
        row.attendance_group_id === groupId &&
        row.course_week_id === weekId
    );
  }

  function getGroupWeek(groupId, weekId) {
    return groupWeeks.find(
      row =>
        row.attendance_group_id === groupId &&
        row.course_week_id === weekId
    ) || null;
  }

  async function handleVariableTimingsChange(checked) {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await updateCourse(course.id, {
        variable_timings_enabled: checked
      });

      setVariableTimings(checked);

      if (onCourseUpdated) {
        onCourseUpdated({
          ...course,
          variable_timings_enabled: checked
        });
      }

      setSuccess(
        `Variable Timings ${checked ? 'enabled' : 'disabled'}.`
      );
    } catch (err) {
      console.error(
        'Variable Timings save failed:',
        err
      );
      setError(
        err?.message ||
        'Failed to save Variable Timings.'
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleVariableRoomsChange(
    checked
  ) {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await updateCourse(
        course.id,
        {
          variable_rooms_enabled:
            checked
        }
      );

      setVariableRooms(
        checked
      );

      if (onCourseUpdated) {
        onCourseUpdated({
          ...course,
          variable_rooms_enabled:
            checked
        });
      }

      setSuccess(
        'Variable Rooms ' +
        (
          checked
            ? 'enabled.'
            : 'disabled.'
        )
      );

    } catch (err) {
      console.error(
        'Variable Rooms save failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to save Variable Rooms.'
      );

    } finally {
      setSaving(false);
    }
  }


  function changeWeekRoomLocally(
    weekId,
    value
  ) {
    setWeeks(
      previous =>
        previous.map(
          row =>
            row.id === weekId
              ? {
                  ...row,
                  classroom_override:
                    value || null
                }
              : row
        )
    );
  }


  async function saveWeekRoom(
    weekId,
    value
  ) {
    setError('');

    try {
      await updateWeek(
        weekId,
        {
          classroom_override:
            value || null
        }
      );

    } catch (err) {
      console.error(
        'Course date room override save failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to save the classroom.'
      );

      await loadAll();
    }
  }


  function changeGroupWeekRoomLocally(
    groupId,
    weekId,
    value
  ) {
    setGroupWeeks(
      previous =>
        previous.map(
          row =>
            row.attendance_group_id ===
              groupId &&
            row.course_week_id ===
              weekId
              ? {
                  ...row,
                  classroom_override:
                    value || null
                }
              : row
        )
    );
  }


  async function saveGroupWeekRoom(
    groupId,
    weekId,
    value
  ) {
    setError('');

    try {
      await updateGroupWeek(
        groupId,
        weekId,
        {
          classroom_override:
            value || null
        }
      );

    } catch (err) {
      console.error(
        'Group attendance room override save failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to save the group classroom.'
      );

      await loadAll();
    }
  }


  function changeWeekTimeLocally(weekId, value) {
    setWeeks(prev => prev.map(row =>
      row.id === weekId
        ? {
            ...row,
            start_time_override: value || null
          }
        : row
    ));
  }

  async function saveWeekTime(weekId, value) {
    setError('');

    try {
      await updateWeek(weekId, {
        start_time_override: value || null
      });
    } catch (err) {
      console.error(
        'Course date time override save failed:',
        err
      );
      setError(
        err?.message ||
        'Failed to save the start time.'
      );
      await loadAll();
    }
  }

  function changeGroupWeekTimeLocally(
    groupId,
    weekId,
    value
  ) {
    setGroupWeeks(prev => prev.map(row =>
      row.attendance_group_id === groupId &&
      row.course_week_id === weekId
        ? {
            ...row,
            start_time_override: value || null
          }
        : row
    ));
  }

  async function saveGroupWeekTime(
    groupId,
    weekId,
    value
  ) {
    setError('');

    try {
      await updateGroupWeek(
        groupId,
        weekId,
        {
          start_time_override: value || null
        }
      );
    } catch (err) {
      console.error(
        'Group attendance time override save failed:',
        err
      );
      setError(
        err?.message ||
        'Failed to save the group start time.'
      );
      await loadAll();
    }
  }

  async function handleGroupBasedChange(checked) {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await updateCourse(course.id, {
        group_based_attendance: checked
      });

      setGroupBased(checked);
      if (!checked) setActiveTab('schedule');

      if (onCourseUpdated) {
        onCourseUpdated({
          ...course,
          group_based_attendance: checked
        });
      }

      setSuccess(
        `Group-Based Attendance ${checked ? 'enabled' : 'disabled'}.`
      );
    } catch (err) {
      console.error('Group-Based Attendance save failed:', err);
      setError(
        err?.message || 'Failed to save Group-Based Attendance.'
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleAutomatedCheckInChange(checked) {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await updateCourse(course.id, {
        automated_checkin_enabled: checked
      });

      setAutomatedCheckIn(checked);

      if (onCourseUpdated) {
        onCourseUpdated({
          ...course,
          automated_checkin_enabled: checked
        });
      }

      setSuccess(
        `Automated Check-In ${checked ? 'enabled' : 'disabled'}.`
      );
    } catch (err) {
      console.error('Automated Check-In save failed:', err);
      setError(
        err?.message || 'Failed to save Automated Check-In.'
      );
    } finally {
      setSaving(false);
    }
  }


  async function handleGlobalWeekToggle(week) {
    const next = !week.attendance_enabled;
    setError('');

    try {
      await updateWeek(week.id, {
        attendance_enabled: next
      });

      setWeeks(prev => prev.map(row =>
        row.id === week.id
          ? { ...row, attendance_enabled: next }
          : row
      ));
    } catch (err) {
      console.error('Attendance week save failed:', err);
      setError(err?.message || 'Failed to update attendance week.');
    }
  }

  async function handleGroupWeekToggle(group, week) {
    const enabled = groupHasWeek(group.id, week.id);
    setError('');

    try {
      if (enabled) {
        await removeGroupWeek(group.id, week.id);
        setGroupWeeks(prev => prev.filter(
          row => !(
            row.attendance_group_id === group.id &&
            row.course_week_id === week.id
          )
        ));
      } else {
        const result = await addGroupWeeks(group.id, [week.id]);
        const saved = result?.[0] || {
          attendance_group_id: group.id,
          course_week_id: week.id
        };
        setGroupWeeks(prev => [...prev, saved]);
      }
    } catch (err) {
      console.error('Group attendance week save failed:', err);
      setError(
        err?.message || 'Failed to update group attendance schedule.'
      );
    }
  }

  async function handleAddGroup() {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const nextGroupNumber = groups.length
        ? Math.max(...groups.map(group => Number(group.group_number) || 0)) + 1
        : 1;

      const result = await createAttendanceGroup(
        course.id,
        nextGroupNumber
      );
      const created = result?.[0];

      if (!created) {
        throw new Error('The attendance group was not created.');
      }

      const defaultWeekIds = weeks
        .filter(week => week.attendance_enabled)
        .map(week => week.id);

      if (defaultWeekIds.length) {
        await addGroupWeeks(created.id, defaultWeekIds);
      }

      await loadAll();
      setSuccess(`Group ${nextGroupNumber} created.`);
    } catch (err) {
      console.error('Attendance group creation failed:', err);
      setError(err?.message || 'Failed to create attendance group.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteGroup(group) {
    const memberCount = membersForGroup(group.id).length;
    const confirmed = window.confirm(
      `Delete ${attendanceGroupLabel(group)}?` +
      (memberCount
        ? ` Its ${memberCount} student${memberCount === 1 ? '' : 's'} will become unassigned.`
        : '')
    );

    if (!confirmed) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await deleteAttendanceGroup(group.id);
      await loadAll();
      setSuccess(`${attendanceGroupLabel(group)} deleted.`);
    } catch (err) {
      console.error('Attendance group deletion failed:', err);
      setError(err?.message || 'Failed to delete attendance group.');
    } finally {
      setSaving(false);
    }
  }

  function toggleSelected(enrolmentId) {
    setSelectedIds(prev =>
      prev.includes(enrolmentId)
        ? prev.filter(id => id !== enrolmentId)
        : [...prev, enrolmentId]
    );
  }

  async function handleAddSelected(group) {
    if (!selectedIds.length) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await addAttendanceMemberships(group.id, selectedIds);
      await loadAll();
      setSuccess(
        `${selectedIds.length} student${selectedIds.length === 1 ? '' : 's'} added to ${attendanceGroupLabel(group)}.`
      );
    } catch (err) {
      console.error('Adding attendance group members failed:', err);
      setError(
        err?.message || 'Failed to add students to attendance group.'
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveMember(group, enrolmentId) {
    setSaving(true);
    setError('');

    try {
      await removeAttendanceMembership(group.id, enrolmentId);
      await loadAll();
    } catch (err) {
      console.error('Removing attendance group member failed:', err);
      setError(
        err?.message || 'Failed to remove student from attendance group.'
      );
    } finally {
      setSaving(false);
    }
  }

  const copySources = useMemo(() => {
    const sources = [];

    const streams = [...new Set(
      enrolments
        .map(enrolment => enrolment.stream)
        .filter(value => value !== null && value !== undefined)
    )].sort((a, b) => Number(a) - Number(b));

    streams.forEach(stream => {
      const streamMembers = enrolments.filter(
        enrolment => Number(enrolment.stream) === Number(stream)
      );

      sources.push({
        key: `stream-${stream}`,
        category: 'Streams',

        displayName:
          getStreamDisplayName(
            coursePlacementLabels,
            stream
          ),

        label:
          `${getStreamDisplayName(
            coursePlacementLabels,
            stream
          )} — all students`,

        enrolmentIds:
          streamMembers.map(
            enrolment =>
              enrolment.id
          )
      });

      const streamGroups = [...new Set(
        streamMembers
          .map(enrolment => enrolment.group_number)
          .filter(value => value !== null && value !== undefined)
      )].sort((a, b) => Number(a) - Number(b));

      streamGroups.forEach(groupNumber => {
        sources.push({
          key: `stream-${stream}-group-${groupNumber}`,
          category: 'Streams',

          displayName:
            getCourseGroupDisplayName(
              coursePlacementLabels,
              stream,
              groupNumber
            ),

          label:
            `${getStreamDisplayName(
              coursePlacementLabels,
              stream
            )} — ${getCourseGroupDisplayName(
              coursePlacementLabels,
              stream,
              groupNumber
            )}`,

          enrolmentIds:
            streamMembers
              .filter(
                enrolment =>
                  Number(
                    enrolment.group_number
                  ) ===
                  Number(groupNumber)
              )
              .map(
                enrolment =>
                  enrolment.id
              )
        });
      });
    });

    courseAssignments
      .filter(
        assignment =>
          assignment.groups_enabled &&
          Number(assignment.group_count) > 0
      )
      .forEach(assignment => {
        const label = assignmentLabel(
          assignment,
          courseAssignments
        );

        for (
          let groupNumber = 1;
          groupNumber <= Number(assignment.group_count);
          groupNumber += 1
        ) {
          const activeEnrolmentIds = new Set(
            enrolments.map(enrolment => enrolment.id)
          );

          const ids = assignmentMemberships
            .filter(
              row =>
                row.course_assignment_id === assignment.id &&
                Number(row.group_number) === groupNumber &&
                activeEnrolmentIds.has(row.enrolment_id)
            )
            .map(row => row.enrolment_id);

          const groupDisplayName =
            getAssignmentGroupDisplayName(
              assignmentGroupLabels,
              assignment.id,
              groupNumber
            );

          sources.push({
            key: `assignment-${assignment.id}-group-${groupNumber}`,
            category: 'Assignment Groups',

            displayName:
              groupDisplayName,

            label:
              `${label} — ${groupDisplayName}`,

            enrolmentIds:
              ids
          });
        }
      });

    return sources;
  }, [
    enrolments,
    courseAssignments,
    assignmentMemberships,
    assignmentGroupLabels,
    coursePlacementLabels
  ]);

  // ATTENDANCE_GROUP_INFERRED_NAMES_20260919
  function sameMembership(
    leftIds,
    rightIds
  ) {
    const left =
      [...new Set(
        leftIds
          .filter(Boolean)
          .map(String)
      )].sort();

    const right =
      [...new Set(
        rightIds
          .filter(Boolean)
          .map(String)
      )].sort();


    if (
      left.length === 0 ||
      left.length !== right.length
    ) {
      return false;
    }


    return left.every(
      (id, index) =>
        id === right[index]
    );
  }


  function attendanceGroupLabel(
    group
  ) {
    if (!group) {
      return 'Group';
    }


    const memberIds =
      memberships
        .filter(
          row =>
            String(
              row.attendance_group_id
            ) ===
            String(group.id)
        )
        .map(
          row =>
            row.enrolment_id
        );


    if (
      memberIds.length > 0
    ) {
      const matches =
        copySources
          .filter(
            candidate =>
              sameMembership(
                memberIds,
                candidate.enrolmentIds ||
                  []
              )
          )
          .sort(
            (a, b) => {
              /*
               * If exactly the same students
               * correspond to more than one
               * named structure, prefer an
               * Assignment Group name.
               */
              const aPriority =
                a.category ===
                'Assignment Groups'
                  ? 0
                  : 1;

              const bPriority =
                b.category ===
                'Assignment Groups'
                  ? 0
                  : 1;

              return (
                aPriority -
                  bPriority ||
                String(
                  a.displayName ||
                  ''
                ).localeCompare(
                  String(
                    b.displayName ||
                    ''
                  ),
                  undefined,
                  {
                    numeric: true,
                    sensitivity: 'base'
                  }
                )
              );
            }
          );


      const match =
        matches.find(
          candidate =>
            String(
              candidate
                .displayName ||
              ''
            ).trim()
        );


      if (match) {
        return String(
          match.displayName
        ).trim();
      }
    }


    return (
      'Group ' +
      (
        Number(
          group.group_number
        ) || 1
      )
    );
  }


  async function handleCopySource(group, source) {
    const uniqueIds = [...new Set(source.enrolmentIds)];
    const currentMembers = membersForGroup(group.id);
    const conflicts = uniqueIds.filter(enrolmentId => {
      const membership = membershipByEnrolment.get(enrolmentId);
      return (
        membership &&
        membership.attendance_group_id !== group.id
      );
    }).length;

    const confirmed = window.confirm(
      `Replace Group ${group.group_number}'s current membership ` +
      `(${currentMembers.length}) with ${source.label} (${uniqueIds.length})?` +
      (conflicts
        ? ` ${conflicts} student${conflicts === 1 ? '' : 's'} will be moved from another attendance group.`
        : '')
    );

    if (!confirmed) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await deleteMembershipsForGroup(group.id);
      await deleteMembershipsForEnrolments(uniqueIds);

      if (uniqueIds.length) {
        await addAttendanceMemberships(group.id, uniqueIds);
      }

      setCopyTargetGroupId(null);
      await loadAll();
      setSuccess(
        `${attendanceGroupLabel(group)} copied from ${source.label}.`
      );
    } catch (err) {
      console.error('Copying attendance group failed:', err);
      setError(
        err?.message || 'Failed to copy attendance group membership.'
      );
    } finally {
      setSaving(false);
    }
  }

      const previewEnrolment = enrolments.find(
    enrolment => enrolment.id === previewEnrolmentId
  ) || null;

  const previewTotalRequired = getRequiredAttendanceCount(
    groupBased ? previewEnrolment : null
  );

  const previewWeight = Number.isFinite(Number(weightDraft))
    ? Number(weightDraft)
    : 0;

  const previewGrace = Math.max(
    0,
    Math.floor(Number(graceDraft) || 0)
  );

  const previewMinimum = Number.isFinite(Number(minContributionDraft))
    ? Number(minContributionDraft)
    : 0;

  const previewIncrease = Number.isFinite(Number(penaltyIncreaseDraft))
    ? Number(penaltyIncreaseDraft)
    : 0;

  const previewRows = buildAttendanceContributionRows({
    totalRequired: previewTotalRequired,
    weight: previewWeight,
    graceAbsences: previewGrace,
    minContribution: previewMinimum,
    penaltyPattern,
    penaltyIncreasePercent: previewIncrease
  });

  async function handleSaveCalculations() {
  const nextGrace = Number(graceDraft);
  const nextMinimum = Number(minContributionDraft);
  const nextIncrease = Number(penaltyIncreaseDraft);

  if (
    !Number.isFinite(nextGrace) ||
    nextGrace < 0 ||
    !Number.isInteger(nextGrace)
  ) {
    setError('Grace absences must be a whole number of 0 or more.');
    return;
  }

  if (!Number.isFinite(nextMinimum)) {
    setError('Minimum attendance contribution must be a number.');
    return;
  }

  if (nextMinimum > previewWeight) {
    setError(
      'Minimum attendance contribution cannot be greater than the attendance contribution.'
    );
    return;
  }

  if (!['linear', 'increasing'].includes(penaltyPattern)) {
    setError('Choose a valid attendance penalty pattern.');
    return;
  }

  if (
    !Number.isFinite(nextIncrease) ||
    nextIncrease < 0 ||
    nextIncrease > 100
  ) {
    setError('Penalty increase must be between 0% and 100%.');
    return;
  }

  if (
    penaltyPattern === 'increasing' &&
    nextIncrease <= 0
  ) {
    setError(
      'Increasing penalties require an increase greater than 0%. Use Linear for a constant penalty.'
    );
    return;
  }

  setWeightSaving(true);
  setError('');
  setSuccess('');

  try {
    await updateCourse(course.id, {
      attendance_grace_absences: nextGrace,
      attendance_min_contribution: nextMinimum,
      attendance_penalty_pattern: penaltyPattern,
      attendance_penalty_increase_percent: nextIncrease
    });

    setGraceDraft(String(nextGrace));
    setMinContributionDraft(String(nextMinimum));
    setPenaltyIncreaseDraft(String(nextIncrease));

    if (onCourseUpdated) {
      onCourseUpdated({
        ...course,
        attendance_grace_absences: nextGrace,
        attendance_min_contribution: nextMinimum,
        attendance_penalty_pattern: penaltyPattern,
        attendance_penalty_increase_percent: nextIncrease
      });
    }

    setSuccess('Attendance calculations saved.');
  } catch (err) {
    console.error('Attendance calculations save failed:', err);

    setError(
      err?.message ||
        'Failed to save attendance calculations.'
    );
  } finally {
    setWeightSaving(false);
  }
}

  async function handleCopyAttendanceTable() {
    const html = buildAttendanceContributionTableHtml({
      totalRequired: previewTotalRequired,
      weight: previewWeight,
      graceAbsences: previewGrace,
      minContribution: previewMinimum,
      penaltyPattern,
      penaltyIncreasePercent: previewIncrease
    });

    const plainText = [
      'Absences\tContribution to final grade',
      ...previewRows.map(
        row =>
          `${row.absences}\t${formatAttendanceContribution(row.contribution)}`
      )
    ].join('\n');

    try {
      if (
        navigator.clipboard?.write &&
        typeof window.ClipboardItem !== 'undefined'
      ) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([plainText], { type: 'text/plain' })
          })
        ]);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(plainText);
      } else {
        throw new Error('Clipboard access is unavailable in this browser.');
      }

      setSuccess('Attendance calculation table copied.');
      setError('');
    } catch (err) {
      console.error('Copy attendance table failed:', err);
      setError(
        err?.message || 'Could not copy the attendance calculation table.'
      );
    }
  }

  if (loading) {
    return <div style={styles.muted}>Loading attendance settings...</div>;
  }

  const copyTarget = groups.find(
    group => group.id === copyTargetGroupId
  ) || null;

  return (
    <div>
      <div style={styles.topRow}>
        <div style={styles.topControl}>
          <div>
            <div style={styles.topLabel}>Group-Based Attendance</div>
            <div style={styles.topHint}>
              Give different attendance requirements to different groups.
            </div>
          </div>

          <Toggle
            checked={groupBased}
            disabled={saving}
            onChange={handleGroupBasedChange}
          />
        </div>

        <div style={styles.topControl}>
          <div>
            <div style={styles.topLabel}>Automated Check-In</div>
            <div style={styles.topHint}>
              Allow students to check in from the mobile attendance system for this course.
            </div>
          </div>

          <Toggle
            checked={automatedCheckIn}
            disabled={saving || course.attendance_enabled === false}
            onChange={handleAutomatedCheckInChange}
          />
        </div>

        <div style={styles.topControl}>
          <div>
            <div style={styles.topLabel}>Variable Timings</div>
            <div style={styles.topHint}>
              Set different start times for individual course dates.
            </div>
          </div>

          <Toggle
            checked={variableTimings}
            disabled={saving}
            onChange={handleVariableTimingsChange}
          />
        </div>

        <div style={styles.topControl}>
          <div>
            <div style={styles.topLabel}>Variable Rooms</div>
            <div style={styles.topHint}>
              Set different rooms for individual course dates or attendance groups.
            </div>
          </div>

          <Toggle
            checked={variableRooms}
            disabled={saving}
            onChange={handleVariableRoomsChange}
          />
        </div>
      </div>

{course.grading_mode === 'traditional' && attendanceType && (
  <section style={styles.calculationsCard}>
    <div style={styles.calculationsHeader}>
      <div>
        <h3 style={styles.calculationsTitle}>
          Attendance Calculations
        </h3>

        <div style={styles.topHint}>
          Set how absences change attendance's contribution to the final course grade.
        </div>
      </div>

      <button
        type="button"
        style={styles.btn}
        disabled={weightSaving}
        onClick={handleSaveCalculations}
      >
        {weightSaving ? 'Saving...' : 'Save Calculations'}
      </button>
    </div>

    <div style={styles.calculationsGrid}>
      <div style={styles.calculationField}>
  <span style={styles.calculationLabel}>
    Attendance is worth
  </span>

  <div style={styles.readOnlyValue}>
    {formatAttendanceContribution(previewWeight)} of final grade
  </div>

  <span style={styles.calculationHelp}>
    Set in the course grading weights.
  </span>
</div>
      <label style={styles.calculationField}>
        <span style={styles.calculationLabel}>
          Grace absences
        </span>

        <input
          type="number"
          min="0"
          step="1"
          style={styles.weightInput}
          value={graceDraft}
          onChange={event =>
            setGraceDraft(event.target.value)
          }
        />

        <span style={styles.calculationHelp}>
          These absences do not reduce the attendance contribution.
        </span>
      </label>

      <label style={styles.calculationField}>
        <span style={styles.calculationLabel}>
          Minimum attendance contribution
        </span>

        <div style={styles.inputWithSuffix}>
          <input
            type="number"
            step="0.1"
            style={styles.weightInput}
            value={minContributionDraft}
            onChange={event =>
              setMinContributionDraft(event.target.value)
            }
          />
          <span>%</span>
        </div>

        <span style={styles.calculationHelp}>
          May be negative if repeated absence should reduce the overall course grade.
        </span>
      </label>

      <div style={styles.calculationField}>
        <span style={styles.calculationLabel}>
          Penalty pattern
        </span>

        <div style={styles.radioRow}>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              name="attendance-penalty-pattern"
              value="linear"
              checked={penaltyPattern === 'linear'}
              onChange={() =>
                setPenaltyPattern('linear')
              }
            />
            Linear
          </label>

          <label style={styles.radioLabel}>
            <input
              type="radio"
              name="attendance-penalty-pattern"
              value="increasing"
              checked={penaltyPattern === 'increasing'}
              onChange={() =>
                setPenaltyPattern('increasing')
              }
            />
            Increasing
          </label>
        </div>

        <span style={styles.calculationHelp}>
          Linear makes every penalized absence cost the same amount.
        </span>
      </div>

      <label
        style={{
          ...styles.calculationField,
          ...(penaltyPattern !== 'increasing'
            ? styles.calculationFieldDisabled
            : {})
        }}
      >
        <span style={styles.calculationLabel}>
          Penalty increase per additional absence
        </span>

        <div style={styles.inputWithSuffix}>
          <input
            type="number"
            min="0"
            max="100"
            step="1"
            disabled={penaltyPattern !== 'increasing'}
            style={styles.weightInput}
            value={penaltyIncreaseDraft}
            onChange={event =>
              setPenaltyIncreaseDraft(event.target.value)
            }
          />
          <span>%</span>
        </div>

        <span style={styles.calculationHelp}>
          {penaltyPattern === 'increasing'
            ? `Each successive penalized absence costs ${previewIncrease}% more than the previous one.`
            : 'Used only when Increasing is selected.'}
        </span>
      </label>
    </div>

    <div style={styles.previewBox}>
      <div style={styles.previewHeader}>
        <div>
          <div style={styles.previewTitle}>
            Live Preview
          </div>

          <div style={styles.calculationHelp}>
            This is the same table that can be inserted dynamically into the syllabus with{' '}
            <code>
              {'{{attendance_calculation_table}}'}
            </code>.
          </div>
        </div>

        <button
          type="button"
          style={styles.copyBtn}
          onClick={handleCopyAttendanceTable}
        >
          Copy Table
        </button>
      </div>

      {groupBased && (
        <label style={styles.previewStudentRow}>
          <span style={styles.calculationLabel}>
            Preview for
          </span>

          <select
            style={styles.previewSelect}
            value={previewEnrolmentId}
            onChange={event =>
              setPreviewEnrolmentId(event.target.value)
            }
          >
            {enrolments.map(enrolment => (
              <option
                key={enrolment.id}
                value={enrolment.id}
              >
                {studentLabel(enrolment)}
              </option>
            ))}
          </select>
        </label>
      )}

      <div style={styles.previewMeta}>
        {previewTotalRequired}{' '}
        required class
        {previewTotalRequired === 1 ? '' : 'es'}
        {groupBased && previewEnrolment
          ? ' for this student'
          : ' in this course'}
      </div>

      {previewTotalRequired > 0 ? (
        <div style={styles.previewTableWrap}>
          <table style={styles.previewTable}>
            <thead>
              <tr>
                <th style={styles.previewTh}>
                  Absences
                </th>
                <th style={styles.previewTh}>
                  Contribution to final grade
                </th>
              </tr>
            </thead>

            <tbody>
              {previewRows.map(row => (
                <tr key={row.absences}>
                  <td style={styles.previewTd}>
                    {row.absences}
                  </td>
                  <td style={styles.previewTd}>
                    {formatAttendanceContribution(
                      row.contribution
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={styles.emptyBox}>
          No attendance requirements are currently assigned for this preview.
        </div>
      )}
    </div>
  </section>
)}
          <div style={styles.tabs}>
        <button
          type="button"
          style={{
            ...styles.tab,
            ...(activeTab === 'schedule' ? styles.tabActive : {})
          }}
          onClick={() => setActiveTab('schedule')}
        >
          Attendance Schedule
        </button>

        {groupBased && (
          <button
            type="button"
            style={{
              ...styles.tab,
              ...(activeTab === 'groups' ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab('groups')}
          >
            Attendance Groups
          </button>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {groupBased && unassigned.length > 0 && (
        <div style={styles.warning}>
          <strong>{unassigned.length}</strong>{' '}
          active student{unassigned.length === 1 ? ' is' : 's are'} not assigned to an attendance group.
          Unassigned students are not counted absent; they have no group attendance requirements until assigned.
        </div>
      )}

      {activeTab === 'schedule' && !groupBased && (
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div>
              <h3 style={styles.sectionHeading}>Attendance Schedule</h3>
              <div style={styles.muted}>
                Toggle the weeks in which attendance is required.
              </div>
            </div>
            <span style={styles.countPill}>
              {weeks.filter(week => week.attendance_enabled).length} of {weeks.length} weeks enabled
            </span>
          </div>

          <div style={styles.weekGrid}>
            {weeks.map(week => (
              <div
                key={week.id}
                style={styles.weekCell}
              >
                <button
                  type="button"
                  style={{
                    ...styles.weekBtn,
                    ...(week.attendance_enabled
                      ? styles.weekBtnOn
                      : styles.weekBtnOff)
                  }}
                  onClick={() =>
                    handleGlobalWeekToggle(week)
                  }
                  title={week.class_date}
                >
                  <div style={styles.weekNum}>
                    W{week.week_number}
                  </div>

                  <div style={styles.weekDate}>
                    {week.class_date?.slice(5)}
                  </div>

                  <div style={styles.weekDay}>
                    {weekdayShort(week.class_date)}
                  </div>
                </button>

                {variableTimings && (
                  <input
                    type="time"
                    aria-label={
                      `Start time for ${week.class_date}`
                    }
                    style={styles.timeOverride}
                    value={String(
                      week.start_time_override ||
                      normalCourseTime(
                        course,
                        week.class_date
                      ) ||
                      ''
                    ).slice(0, 5)}
                    onChange={event =>
                      changeWeekTimeLocally(
                        week.id,
                        event.target.value
                      )
                    }
                    onBlur={event =>
                      saveWeekTime(
                        week.id,
                        event.target.value
                      )
                    }
                  />
                )}

                {variableRooms && (
                  <input
                    type="text"
                    aria-label={
                      'Room for ' +
                      week.class_date
                    }
                    style={styles.roomOverride}
                    value={
                      week.classroom_override ||
                      normalClassroomForDate(
                        course,
                        week.class_date
                      ) ||
                      ''
                    }
                    placeholder="Room"
                    onChange={event =>
                      changeWeekRoomLocally(
                        week.id,
                        event.target.value
                      )
                    }
                    onBlur={event =>
                      saveWeekRoom(
                        week.id,
                        event.target.value
                      )
                    }
                  />
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {activeTab === 'schedule' && groupBased && (
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div>
              <h3 style={styles.sectionHeading}>Attendance Schedule</h3>
              <div style={styles.muted}>
                A week switched off for a group is not an absence; that group is simply not required to attend that week.
              </div>
            </div>
          </div>

          {groups.length === 0 ? (
            <div style={styles.emptyBox}>
              No attendance groups yet. Open Attendance Groups and create your first group.
            </div>
          ) : (
            <div style={styles.groupScheduleList}>
              {groups.map(group => {
                const enabledCount = weeks.filter(
                  week => groupHasWeek(group.id, week.id)
                ).length;

                return (
                  <div key={group.id} style={styles.groupScheduleRow}>
                    <div style={styles.groupScheduleHeader}>
                      <strong>
                        {attendanceGroupLabel(group)}
                      </strong>
                      <span style={styles.countPill}>
                        {enabledCount} of {weeks.length} weeks enabled
                      </span>
                    </div>

                    <div style={styles.weekGrid}>
                      {weeks.map(week => {
                        const enabled =
                          groupHasWeek(
                            group.id,
                            week.id
                          );

                        const groupWeek =
                          getGroupWeek(
                            group.id,
                            week.id
                          );

                        return (
                          <div
                            key={week.id}
                            style={styles.weekCell}
                          >
                            <button
                              type="button"
                              style={{
                                ...styles.weekBtn,
                                ...(enabled
                                  ? styles.weekBtnOn
                                  : styles.weekBtnOff)
                              }}
                              onClick={() =>
                                handleGroupWeekToggle(
                                  group,
                                  week
                                )
                              }
                              title={week.class_date}
                            >
                              <div style={styles.weekNum}>
                                W{week.week_number}
                              </div>

                              <div style={styles.weekDate}>
                                {week.class_date?.slice(5)}
                              </div>

                              <div style={styles.weekDay}>
                                {weekdayShort(
                                  week.class_date
                                )}
                              </div>
                            </button>

                            {variableTimings &&
                              enabled && (
                              <input
                                type="time"
                                aria-label={
                                  `Start time for ${attendanceGroupLabel(group)} on ${week.class_date}`
                                }
                                style={styles.timeOverride}
                                value={String(
                                  groupWeek
                                    ?.start_time_override ||
                                  normalCourseTime(
                                    course,
                                    week.class_date
                                  ) ||
                                  ''
                                ).slice(0, 5)}
                                onChange={event =>
                                  changeGroupWeekTimeLocally(
                                    group.id,
                                    week.id,
                                    event.target.value
                                  )
                                }
                                onBlur={event =>
                                  saveGroupWeekTime(
                                    group.id,
                                    week.id,
                                    event.target.value
                                  )
                                }
                              />
                            )}

                            {variableRooms &&
                              enabled && (
                              <input
                                type="text"
                                aria-label={
                                  'Room for ' +
                                  attendanceGroupLabel(
                                    group
                                  ) +
                                  ' on ' +
                                  week.class_date
                                }
                                style={styles.roomOverride}
                                value={
                                  groupWeek
                                    ?.classroom_override ||
                                  week
                                    ?.classroom_override ||
                                  normalClassroomForDate(
                                    course,
                                    week.class_date
                                  ) ||
                                  ''
                                }
                                placeholder="Room"
                                onChange={event =>
                                  changeGroupWeekRoomLocally(
                                    group.id,
                                    week.id,
                                    event.target.value
                                  )
                                }
                                onBlur={event =>
                                  saveGroupWeekRoom(
                                    group.id,
                                    week.id,
                                    event.target.value
                                  )
                                }
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {activeTab === 'groups' && groupBased && (
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div>
              <h3 style={styles.sectionHeading}>Attendance Groups</h3>
              <div style={styles.muted}>
                Students can belong to only one attendance group. Select unassigned students, then add them to a group, or copy exact membership from a stream or assignment group.
              </div>
            </div>

            <button
              type="button"
              style={styles.btn}
              disabled={saving}
              onClick={handleAddGroup}
            >
              + Add Group
            </button>
          </div>

          <div style={styles.groupManagerBody}>
            <div style={styles.unassignedPanel}>
              <div style={styles.groupPanelTitle}>
                Unassigned Students
              </div>

              {unassigned.length === 0 ? (
                <div style={styles.emptyGroupText}>
                  All active students are assigned.
                </div>
              ) : (
                <div style={styles.studentPicker}>
                  {unassigned.map(enrolment => (
                    <label
                      key={enrolment.id}
                      style={styles.studentPickerRow}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(enrolment.id)}
                        onChange={() => toggleSelected(enrolment.id)}
                      />
                      <span>{studentLabel(enrolment)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.groupsScroll}>
              <div
                style={{
                  ...styles.groupsGrid,
                  gridTemplateColumns:
                    `repeat(${Math.max(1, groups.length)}, minmax(230px, 1fr))`
                }}
              >
                {groups.map(group => {
                  const members = membersForGroup(group.id);

                  return (
                    <div key={group.id} style={styles.groupColumn}>
                      <div style={styles.groupColumnHeader}>
                        <div>
                          <div style={styles.groupColumnTitle}>
                            {attendanceGroupLabel(group)}
                          </div>
                          <div style={styles.groupCount}>
                            {members.length} student{members.length === 1 ? '' : 's'}
                          </div>
                        </div>

                        <button
                          type="button"
                          style={styles.deleteGroupBtn}
                          disabled={saving}
                          onClick={() => handleDeleteGroup(group)}
                          title="Delete attendance group"
                        >
                          Delete
                        </button>
                      </div>

                      <div style={styles.groupActions}>
                        <button
                          type="button"
                          style={{
                            ...styles.smallBtn,
                            ...(selectedIds.length === 0 || saving
                              ? styles.disabledBtn
                              : {})
                          }}
                          disabled={selectedIds.length === 0 || saving}
                          onClick={() => handleAddSelected(group)}
                        >
                          Add Students
                        </button>

                        <button
                          type="button"
                          style={styles.copyBtn}
                          disabled={saving}
                          onClick={() => setCopyTargetGroupId(group.id)}
                        >
                          Copy from Assignment Group or Stream
                        </button>
                      </div>

                      <div style={styles.groupMemberList}>
                        {members.length === 0 ? (
                          <div style={styles.emptyGroupText}>
                            No students yet.
                          </div>
                        ) : (
                          members.map(enrolment => (
                            <div
                              key={enrolment.id}
                              style={styles.groupMember}
                            >
                              <span>{studentLabel(enrolment)}</span>
                              <button
                                type="button"
                                style={styles.removeMemberBtn}
                                disabled={saving}
                                onClick={() =>
                                  handleRemoveMember(
                                    group,
                                    enrolment.id
                                  )
                                }
                              >
                                Remove
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {copyTarget && (
        <div
          style={styles.modalBackdrop}
          onMouseDown={() => setCopyTargetGroupId(null)}
        >
          <div
            style={styles.modal}
            onMouseDown={event => event.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>
                  Copy membership into {attendanceGroupLabel(copyTarget)}
                </h3>
                <div style={styles.modalHint}>
                  Clicking a source replaces this attendance group's membership exactly. Students already in another attendance group will be moved.
                </div>
              </div>

              <button
                type="button"
                style={styles.closeBtn}
                onClick={() => setCopyTargetGroupId(null)}
              >
                ×
              </button>
            </div>

            {['Streams', 'Assignment Groups'].map(category => {
              const sources = copySources.filter(
                source => source.category === category
              );

              return (
                <div key={category} style={styles.sourceSection}>
                  <div style={styles.sourceHeading}>{category}</div>

                  {sources.length === 0 ? (
                    <div style={styles.emptyGroupText}>
                      No {category.toLowerCase()} available in this course.
                    </div>
                  ) : (
                    <div style={styles.sourceList}>
                      {sources.map(source => (
                        <button
                          key={source.key}
                          type="button"
                          style={styles.sourceBtn}
                          disabled={saving}
                          onClick={() =>
                            handleCopySource(copyTarget, source)
                          }
                        >
                          <span>{source.label}</span>
                          <span style={styles.sourceCount}>
                            {source.enrolmentIds.length} student{source.enrolmentIds.length === 1 ? '' : 's'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  topRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
    marginBottom: '18px'
  },
  topControl: {
    border: '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    background: '#f7f9fa'
  },
  topLabel: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#1f2933',
    marginBottom: '3px'
  },
  topHint: {
    fontSize: '12px',
    color: '#66788a',
    lineHeight: '1.45'
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    fontSize: '13px',
    fontWeight: '700',
    color: '#215b7d',
    whiteSpace: 'nowrap'
  },
  weekCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0
  },

  weekDay: {
    marginTop: '2px',
    fontSize: '9px',
    fontWeight: '700',
    opacity: 0.72,
    textTransform: 'uppercase'
  },

  timeOverride: {
    boxSizing: 'border-box',
    width: '100%',
    minWidth: 0,
    height: '24px',
    padding: '1px 3px',
    border: '1px solid #cbd5df',
    borderRadius: '5px',
    background: 'white',
    color: '#33424f',
    fontSize: '10px'
  },

  roomOverride: {
    boxSizing: 'border-box',
    width: '100%',
    minWidth: 0,
    height: '24px',
    padding: '1px 4px',
    border: '1px solid #cbd5df',
    borderRadius: '5px',
    background: 'white',
    color: '#33424f',
    fontSize: '10px'
  },

  calculationsCard: {
    border: '1px solid #b9cfdd',
    borderRadius: '10px',
    padding: '18px',
    marginBottom: '18px',
    background: '#fbfdfe'
  },
  calculationsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '18px',
    flexWrap: 'wrap'
  },
  calculationsTitle: {
    margin: '0 0 4px',
    fontSize: '17px',
    color: '#1f2933'
  },
  calculationsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '14px'
  },
  calculationField: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '6px',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '12px',
    background: 'white'
  },
  calculationFieldDisabled: {
    opacity: 0.55
  },
  calculationLabel: {
    fontSize: '12px',
    fontWeight: '800',
    color: '#33424f'
  },
  calculationHelp: {
    fontSize: '11px',
    color: '#66788a',
    lineHeight: '1.4'
  },
  inputWithSuffix: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#42515f'
  },
  readOnlyValue: {
    fontSize: '18px',
    fontWeight: '800',
    color: '#215b7d'
  },
  radioRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    minHeight: '32px'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: '700',
    color: '#33424f',
    cursor: 'pointer'
  },
  previewBox: {
    marginTop: '18px',
    paddingTop: '16px',
    borderTop: '1px solid #d7dee5'
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '12px',
    flexWrap: 'wrap'
  },
  previewTitle: {
    fontSize: '14px',
    fontWeight: '800',
    color: '#1f2933',
    marginBottom: '3px'
  },
  previewStudentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px',
    flexWrap: 'wrap'
  },
  previewSelect: {
    minWidth: '240px',
    maxWidth: '100%',
    border: '1px solid #d7dee5',
    borderRadius: '7px',
    padding: '7px 9px',
    fontSize: '12px',
    background: 'white'
  },
  previewMeta: {
    fontSize: '12px',
    color: '#66788a',
    marginBottom: '8px'
  },
  previewTableWrap: {
    overflowX: 'auto'
  },
  previewTable: {
    borderCollapse: 'collapse',
    width: '100%',
    maxWidth: '520px',
    background: 'white'
  },
  previewTh: {
    border: '1px solid #cbd5df',
    padding: '7px 10px',
    background: '#f3f6f8',
    textAlign: 'center',
    fontSize: '12px',
    color: '#33424f'
  },
  previewTd: {
    border: '1px solid #cbd5df',
    padding: '6px 10px',
    textAlign: 'center',
    fontSize: '12px',
    color: '#33424f'
  },
  automatedBtn: {
    border: '1px solid #d7dee5',
    borderRadius: '10px',
    background: '#f3f5f7',
    color: '#66788a',
    padding: '14px 16px',
    fontWeight: '700',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px'
  },
  comingSoon: {
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    background: '#e5e9ed',
    borderRadius: '999px',
    padding: '3px 7px'
  },
  weightRow: {
    border: '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '14px 16px',
    marginBottom: '18px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap'
  },
  weightTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#1f2933',
    marginBottom: '3px'
  },
  weightControl: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  weightInput: {
    width: '90px',
    border: '1px solid #d7dee5',
    borderRadius: '7px',
    padding: '7px 9px',
    fontSize: '13px'
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    borderBottom: '2px solid #d7dee5',
    marginBottom: '20px'
  },
  tab: {
    border: 'none',
    background: 'transparent',
    padding: '9px 14px',
    fontSize: '13px',
    fontWeight: '700',
    color: '#66788a',
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px'
  },
  tabActive: {
    color: '#215b7d',
    borderBottomColor: '#215b7d'
  },
  error: {
    background: '#faeaea',
    border: '1px solid #e8bcbc',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#a12a2a',
    fontSize: '13px',
    marginBottom: '14px'
  },
  success: {
    background: '#e7f5ed',
    border: '1px solid #b9dfc9',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#216e45',
    fontSize: '13px',
    marginBottom: '14px'
  },
  warning: {
    background: '#fff7df',
    border: '1px solid #ead397',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#76530b',
    fontSize: '13px',
    lineHeight: '1.5',
    marginBottom: '16px'
  },
  section: {
    marginBottom: '12px'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '14px',
    flexWrap: 'wrap'
  },
  sectionHeading: {
    margin: '0 0 4px',
    fontSize: '16px',
    color: '#1f2933'
  },
  muted: {
    color: '#66788a',
    fontSize: '13px',
    lineHeight: '1.45'
  },
  countPill: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: '999px',
    padding: '4px 9px',
    background: '#edf3f7',
    color: '#52616d',
    fontSize: '11px',
    fontWeight: '700',
    whiteSpace: 'nowrap'
  },
  weekGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '7px'
  },
  weekBtn: {
    width: '62px',
    minHeight: '50px',
    borderRadius: '8px',
    border: '1px solid #d7dee5',
    cursor: 'pointer',
    padding: '6px 4px',
    transition: 'all 0.15s ease'
  },
  weekBtnOn: {
    background: '#e7f1f7',
    borderColor: '#93bfd7',
    color: '#215b7d'
  },
  weekBtnOff: {
    background: '#f6f7f8',
    color: '#8a97a3'
  },
  weekNum: {
    fontSize: '12px',
    fontWeight: '800'
  },
  weekDate: {
    fontSize: '10px',
    marginTop: '2px'
  },
  groupScheduleList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },
  groupScheduleRow: {
    border: '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '14px',
    background: '#fbfcfd'
  },
  groupScheduleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px',
    color: '#1f2933'
  },
  emptyBox: {
    border: '1px dashed #c8d1d9',
    borderRadius: '10px',
    padding: '18px',
    color: '#66788a',
    fontSize: '13px',
    background: '#fafbfc'
  },
  btn: {
    background: '#215b7d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  smallBtn: {
    background: '#215b7d',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '6px 9px',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  copyBtn: {
    background: 'white',
    color: '#215b7d',
    border: '1px solid #b0cfe0',
    borderRadius: '6px',
    padding: '6px 9px',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  disabledBtn: {
    opacity: 0.45,
    cursor: 'default'
  },
  groupManagerBody: {
    display: 'grid',
    gridTemplateColumns: '260px minmax(0, 1fr)',
    gap: '16px',
    alignItems: 'start'
  },
  unassignedPanel: {
    border: '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '12px',
    background: '#f7f9fa'
  },
  groupPanelTitle: {
    fontSize: '13px',
    fontWeight: '800',
    color: '#1f2933',
    marginBottom: '9px'
  },
  studentPicker: {
    maxHeight: '500px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  studentPickerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    padding: '5px 2px',
    fontSize: '12px',
    color: '#33424f',
    cursor: 'pointer'
  },
  groupsScroll: {
    overflowX: 'auto',
    paddingBottom: '4px'
  },
  groupsGrid: {
    display: 'grid',
    gap: '12px',
    alignItems: 'start',
    minWidth: '100%'
  },
  groupColumn: {
    border: '1px solid #b0cfe0',
    borderRadius: '10px',
    padding: '12px',
    background: '#f7fbff',
    minHeight: '170px'
  },
  groupColumnHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '8px',
    borderBottom: '1px solid #d8e8f1',
    paddingBottom: '8px',
    marginBottom: '8px'
  },
  groupColumnTitle: {
    fontSize: '13px',
    fontWeight: '800',
    color: '#215b7d'
  },
  groupCount: {
    fontSize: '10px',
    color: '#66788a',
    marginTop: '2px'
  },
  deleteGroupBtn: {
    background: 'transparent',
    border: 'none',
    color: '#a12a2a',
    fontSize: '10px',
    fontWeight: '700',
    cursor: 'pointer',
    padding: '2px'
  },
  groupActions: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    marginBottom: '10px'
  },
  groupMemberList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  groupMember: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    background: 'white',
    borderRadius: '6px',
    padding: '6px 7px',
    fontSize: '11px',
    color: '#33424f'
  },
  removeMemberBtn: {
    background: 'transparent',
    color: '#a12a2a',
    border: 'none',
    fontSize: '10px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  emptyGroupText: {
    fontSize: '12px',
    color: '#7b8792',
    fontStyle: 'italic',
    padding: '5px 0'
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28, 39, 49, 0.48)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px'
  },
  modal: {
    width: 'min(720px, 96vw)',
    maxHeight: '82vh',
    overflowY: 'auto',
    background: 'white',
    borderRadius: '12px',
    padding: '18px',
    boxShadow: '0 20px 50px rgba(0,0,0,0.24)'
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '16px'
  },
  modalTitle: {
    margin: '0 0 4px',
    color: '#1f2933',
    fontSize: '17px'
  },
  modalHint: {
    color: '#66788a',
    fontSize: '12px',
    lineHeight: '1.45'
  },
  closeBtn: {
    border: 'none',
    background: 'transparent',
    color: '#66788a',
    fontSize: '24px',
    cursor: 'pointer',
    lineHeight: 1
  },
  sourceSection: {
    marginTop: '14px'
  },
  sourceHeading: {
    fontSize: '12px',
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#52616d',
    marginBottom: '7px'
  },
  sourceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  sourceBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    width: '100%',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    background: '#fbfcfd',
    color: '#253746',
    padding: '9px 11px',
    fontSize: '12px',
    cursor: 'pointer',
    textAlign: 'left'
  },
  sourceCount: {
    color: '#66788a',
    fontSize: '10px',
    whiteSpace: 'nowrap'
  }
};